const ACTIVE_SUBSCRIPTION_TYPES = new Set(["trial", "subscribed", "perpetual"]);
const NORMALIZED_SUBSCRIPTION_SOURCES = {
  stripe: "stripe",
  billing: "stripe",
  meta: "meta",
  manual: "manual",
  perpetual: "manual",
  system: "system",
};

export const normalizeSubscriptionStatus = (status) => {
  if (!status) {
    return null;
  }

  const normalizedStatus = String(status).trim().toLowerCase();

  if (normalizedStatus === "active") {
    return "subscribed";
  }

  if (normalizedStatus === "expired") {
    return "free";
  }

  return normalizedStatus;
};

export const parseSubscriptionDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

export const normalizeSubscriptionSource = (source) => {
  if (!source) {
    return null;
  }

  const normalizedSource = String(source).trim().toLowerCase();
  return NORMALIZED_SUBSCRIPTION_SOURCES[normalizedSource] || normalizedSource;
};

export const getSubscriptionState = (
  {
    status,
    source,
    expirationDate,
    renews,
    autoRenew,
    cancelAtPeriodEnd,
  } = {},
  now = new Date(),
) => {
  const normalizedStatus = normalizeSubscriptionStatus(status);
  const normalizedSource = normalizeSubscriptionSource(source);
  const parsedExpirationDate = parseSubscriptionDate(expirationDate);
  const isPerpetual = normalizedStatus === "perpetual";
  const isMissingRequiredExpiration =
    ACTIVE_SUBSCRIPTION_TYPES.has(normalizedStatus) &&
    !isPerpetual &&
    !parsedExpirationDate;
  const isExpired =
    normalizedStatus === "free" ||
    isMissingRequiredExpiration ||
    (!isPerpetual &&
      ACTIVE_SUBSCRIPTION_TYPES.has(normalizedStatus) &&
      parsedExpirationDate &&
      parsedExpirationDate.getTime() < now.getTime());
  const hasClassroomAccess =
    ACTIVE_SUBSCRIPTION_TYPES.has(normalizedStatus) &&
    (isPerpetual || !isExpired);
  const hasActiveRecurringSubscription =
    normalizedStatus === "subscribed" &&
    hasClassroomAccess;
  const effectiveRenews =
    hasActiveRecurringSubscription
      ? typeof renews === "boolean"
        ? renews
        : typeof autoRenew === "boolean"
        ? autoRenew
        : normalizedSource === "stripe"
        ? !Boolean(cancelAtPeriodEnd)
        : normalizedSource === "meta"
        ? true
        : false
      : false;
  const willCancelAtPeriodEnd =
    hasActiveRecurringSubscription &&
    normalizedSource === "stripe" &&
    !effectiveRenews;

  return {
    status: isExpired ? "free" : normalizedStatus,
    source: normalizedSource,
    expirationDate: parsedExpirationDate,
    cancelAtPeriodEnd: willCancelAtPeriodEnd,
    renews: effectiveRenews,
    autoRenew: effectiveRenews,
    hasClassroomAccess,
    isTrial: normalizedStatus === "trial" && hasClassroomAccess,
    isSubscribed:
      (normalizedStatus === "subscribed" || normalizedStatus === "perpetual") &&
      hasClassroomAccess,
    isFree: !hasClassroomAccess,
  };
};

export const formatSubscriptionDate = (value) => {
  const parsedDate = parseSubscriptionDate(value);

  if (!parsedDate) {
    return "Unknown";
  }

  return parsedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatRoleName = (roleName) => {
  if (!roleName) {
    return "Account holder";
  }

  return roleName
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
};

export const getRoleDisplayName = ({
  roleName,
  roleId,
  isRoleNameLoading = false,
} = {}) => {
  if (roleName) {
    return formatRoleName(roleName);
  }

  if (roleId && isRoleNameLoading) {
    return "Loading role...";
  }

  if (roleId) {
    return "Role unavailable";
  }

  return formatRoleName(roleName);
};

export const formatSubscriptionLabel = (status, hasClassroomAccess) => {
  const normalizedStatus = normalizeSubscriptionStatus(status);

  if (normalizedStatus === "trial" && hasClassroomAccess) {
    return "Trial";
  }

  if (
    (normalizedStatus === "subscribed" || normalizedStatus === "perpetual") &&
    hasClassroomAccess
  ) {
    return "Subscribed";
  }

  return "Free";
};
